import React, { useCallback, useState } from 'react';
import {
  Image,
  LayoutChangeEvent,
  Pressable,
  StyleSheet,
  View,
} from 'react-native';
import { Category, CategoryKey } from '../../data/services';
import { colors, hairline, radii, spacing } from '../../theme';
import { useAreaAvailability } from '../../hooks/useAreaAvailability';
import Icon from '../ui/Icon';
import Text from '../ui/Text';

/**
 * The categories, as a wrapping grid rather than a horizontal rail.
 *
 * A rail keeps the block one row deep whatever the count, but it hides
 * everything past the third tile behind a swipe most people never make. There
 * are only eight categories and they are the spine of the app - a grid shows
 * all of them at once, and the extra height it costs buys the whole catalogue
 * being visible without a gesture.
 *
 * ---------------------------------------------------------------------------
 * THE PANEL SITS BEHIND THE FIGURE
 * ---------------------------------------------------------------------------
 * The panel is painted first and the artwork over it, so the whole figure
 * shows, standing in front of a card rather than cropped by it. The card is a
 * backdrop; the surplus height escapes above it.
 *
 * Order is the whole trick, and it is fragile: React Native paints siblings in
 * document order, so moving the `Image` above the panel in the JSX would put
 * the card in front and cut the figure off at the chest.
 *
 * ---------------------------------------------------------------------------
 * WHY THE TILE SIZE IS MEASURED RATHER THAN EXPRESSED IN PERCENTAGES
 * ---------------------------------------------------------------------------
 * The artwork is drawn larger than its tile so the figure stands out of the
 * top. Sizing that with a percentage does not work: the value resolves against
 * the parent's height, and a parent sized by `aspectRatio` has no definite
 * height at the time percentages are resolved. The percentage is dropped, the
 * `Image` falls back to its intrinsic size - 512px - and every figure is drawn
 * several times the size of its tile, straddling its neighbours.
 *
 * Measuring the row once and doing the arithmetic in points avoids the whole
 * problem, and costs one extra layout pass on mount.
 */

/*
 * Three across.
 *
 * Four fit the eight categories into two tidy rows and kept each tile to
 * ~85pt, but three is the count this app uses for every grid of pictures -
 * the subcategory sheet is three as well - and one rhythm across the app is
 * worth more than the tighter packing. Eight categories land as 3/3/2, and
 * `rowsOf` holds that last row to the same tile width as the others.
 */
const COLUMNS = 3;
const GAP = spacing.sm;

/** Below this a tile is too narrow for its label to read. */
const MIN_TILE = 72;

/**
 * Above this a tile has outgrown the row it belongs to.
 *
 * On a tablet, extra room should buy more tiles rather than bigger ones: at
 * 700pt three tiles are 230pt each, each carrying a 60pt figure and a one-word
 * label, and the grid reads as three posters rather than a catalogue.
 */
const MAX_TILE = 150;

/**
 * How wide a row must be before splitting it is the right answer.
 *
 * ---------------------------------------------------------------------------
 * A BIG PHONE IS NOT A SMALL TABLET
 * ---------------------------------------------------------------------------
 * Without this gate, `MAX_TILE` fired on large phones: a 448pt handset went to
 * FOUR columns and its tiles shrank from 130pt to 100pt. The bigger the phone,
 * the smaller the categories - which is exactly backwards, and it is what "it
 * looks too small on a big phone" turns out to mean.
 *
 * Three across is a design decision on a phone at any width. Only a row wide
 * enough to be a tablet's gets to reconsider it. 520 sits above every phone
 * this app will meet (a 480pt handset measures ~456 here after the gutters)
 * and below a 600pt tablet's row (~536).
 */
const WIDE_ROW = 520;

/**
 * How much larger the artwork is than its tile.
 *
 * The surplus goes upward (the art is pinned to the tile's floor) and sideways
 * (it is centred), so the figure stands proud of the panel. This only works
 * because the artwork is cutouts - an opaque photograph cannot break out of
 * anything, and cropping one to look as if it does just shows its own edges.
 */
const ART_SCALE = 0.86;

/**
 * Card width as a share of the cell's.
 *
 * Narrower than the artwork standing on it, so the figure overhangs on three
 * sides rather than only the top. A card the full width of its cell reads as
 * the tile itself; inset, it reads as something the figure is standing on.
 */
const CARD_WIDTH = 0.82;

/**
 * Where the artwork's foot sits, as a share of the panel's height measured up
 * from its base.
 *
 * Raising this lifts the figure without growing it: the art keeps its size and
 * simply sits higher on the card, so more of it clears the top edge. Scaling
 * the art up would have done the same thing by making everything bigger, which
 * is the opposite of what a lighter row needs.
 */
const ART_FOOT = 0.02;

/**
 * Tile height as a share of its width.
 *
 * Shorter than square: the panel is a plinth the figure stands on, not a frame
 * around it. The lower it is, the more of the figure clears it.
 */
const TILE_ASPECT = 0.46;

/**
 * How much of a square tile the artwork fills.
 *
 * All of it. A square tile IS a photograph in a frame - that is the whole
 * difference between `square` and the default cutout-on-a-plinth, and it is
 * what the prop's own note says.
 *
 * This was 0.88, reasoned as "so the figure has air on every side". That is
 * the argument for the CUTOUT, which is drawn larger than its card and needs
 * room to escape into; applied to a photograph it inset the picture inside its
 * own frame and left a ring of the card's tint showing on all four sides,
 * which reads as an image that failed to cover its box.
 *
 * The rail this grid replaced on Home had it right and said so: "One, because
 * the artwork is square and the card is its frame."
 */
const SQUARE_ART = 1;

type Props = {
  categories: Category[];
  /** Highlighted entry, if any. */
  selected?: CategoryKey;
  onSelect?: (key: CategoryKey) => void;
  /**
   * Tiles per row. Defaults to the three Home is drawn to.
   *
   * Bookings passes its own count so a short list sits on one line instead of
   * wrapping after three. Clamped at both ends against the measured row:
   * `MIN_TILE` below, because a tile narrower than its own label is worse than
   * a second row, and `MAX_TILE` above, because a tablet should show more
   * tiles rather than bigger ones.
   */
  columns?: number;
  /** Space between tiles. Defaults to the 8pt Home uses. */
  gap?: number;
  /**
   * Whether each figure gets a card to stand on. On by default.
   *
   * Bookings turns it off: there the row stands on the banner behind it, and a
   * card per figure would be a second plinth on top of the one the artwork is
   * already standing on. The lift is unchanged either way, so the figures rise
   * exactly as far - they simply rise out of the banner instead of a card.
   */
  plinth?: boolean;
  /**
   * Square tiles with the picture inside them, rather than a figure standing
   * out of a low plinth.
   *
   * The two are different objects. The default is a cutout on a stage - the
   * artwork is drawn larger than its card and the surplus escapes upward. A
   * square is a framed picture: nothing leaves it, so the row needs no reserved
   * headroom and sits flush under whatever is above it.
   */
  square?: boolean;
};

/** Split into fixed-size rows, so a short last row does not stretch. */
const rowsOf = <T,>(items: T[], size: number): T[][] => {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    rows.push(items.slice(i, i + size));
  }
  return rows;
};

function CategoryGrid({
  categories,
  selected,
  onSelect,
  columns = COLUMNS,
  gap = GAP,
  plinth = true,
  square = false,
}: Props) {
  const [width, setWidth] = useState(0);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    const measured = event.nativeEvent.layout.width;
    setWidth(prev => (Math.abs(prev - measured) > 0.5 ? measured : prev));
  }, []);

  /*
   * Asked-for columns, then moved in whichever direction the measured row
   * needs: down until the tiles are wide enough to read - one line is the
   * intent, not a promise, and six trades on a 320pt screen would be 45pt
   * each - and up while they would otherwise be wider than a tile should be.
   *
   * Both loops are no-ops on a phone at the counts this app asks for, so the
   * grid is unchanged there and only a genuinely wide row moves.
   */
  const tileAt = (n: number) => (width - gap * (n - 1)) / n;
  const cols = Math.max(
    1,
    (() => {
      let n = Math.max(1, Math.round(columns));
      while (n > 1 && width > 0 && tileAt(n) < MIN_TILE) {
        n -= 1;
      }
      /* Only a tablet-width row splits further - see `WIDE_ROW`. On a phone
         the extra width goes into the tiles instead. */
      while (
        width >= WIDE_ROW &&
        tileAt(n) > MAX_TILE &&
        tileAt(n + 1) >= MIN_TILE &&
        n < categories.length
      ) {
        n += 1;
      }
      return n;
    })(),
  );

  const tile = width > 0 ? (width - gap * (cols - 1)) / cols : 0;
  /*
   * Square mode collapses every one of these to the simple case: the card is
   * the tile, the artwork fills it, nothing sits proud of anything, and so
   * there is no lift for the row to reserve.
   */
  const tileHeight = square ? tile : tile * TILE_ASPECT;
  /*
   * With a card behind it the picture is inset, so the card shows as a border.
   * With no card there is nothing to leave room for - the picture is the tile,
   * and the inset would just be a gap.
   */
  const art = square
    ? tile * (plinth ? SQUARE_ART : 1)
    : tile * ART_SCALE;
  const cardWidth = square ? tile : tile * CARD_WIDTH;
  const foot = square ? (tile - art) / 2 : tileHeight * ART_FOOT;
  /** What escapes above the panel, and so what each row must reserve. */
  const lift = square ? 0 : Math.max(0, art + foot - tileHeight);

  const rows = rowsOf(categories, cols);
  /* Categories none of whose services reach this address - see
     `useAreaAvailability`. The tile stays pressable and the screen answers
     the tap with the "coming soon" sheet. */
  const unavailableCategories = useAreaAvailability().categories;

  return (
    <View style={styles.grid} onLayout={onLayout}>
      {/* Nothing to draw until the row has been measured; it lands next frame. */}
      {tile > 0
        ? rows.map((row, rowIndex) => (
            <View key={rowIndex} style={[styles.row, { paddingTop: lift, gap }]}>
              {row.map(category => {
                const active = category.key === selected;

                return (
                  <Pressable
                    key={category.key}
                    accessibilityRole="button"
                    accessibilityState={{ selected: active }}
                    accessibilityLabel={category.label}
                    accessibilityHint={
                      unavailableCategories.has(category.key)
                        ? 'Coming soon to this area'
                        : undefined
                    }
                    onPress={() => onSelect?.(category.key)}
                    style={({ pressed }) => [
                      styles.cell,
                      { width: tile },
                      pressed ? styles.pressed : null,
                      unavailableCategories.has(category.key)
                        ? styles.dim
                        : null,
                    ]}
                  >
                    <View style={{ width: tile, height: tileHeight }}>
                      {/*
                        Painted first: the card is the backdrop.

                        Dropped entirely when `plinth` is off - except for a
                        category with no artwork, which still needs somewhere to
                        put its glyph or the cell would be empty.
                      */}
                      {plinth || !category.image ? (
                        <View
                          style={[
                            styles.panel,
                            {
                              width: cardWidth,
                              height: tileHeight,
                              left: (tile - cardWidth) / 2,
                            },
                            active ? styles.panelActive : null,
                          ]}
                        >
                          {/* Categories with no artwork show their glyph here. */}
                          {category.image ? null : (
                            <Icon
                              name={category.icon}
                              size={28}
                              color={colors.primary}
                              strokeWidth={1.6}
                            />
                          )}
                        </View>
                      ) : null}

                      {/* Over the card, standing on it. */}
                      {category.image ? (
                        <Image
                          source={category.image}
                          style={[
                            styles.art,
                            {
                              width: art,
                              height: art,
                              bottom: foot,
                              left: (tile - art) / 2,
                            },
                            square ? styles.artSquare : null,
                          ]}
                          /*
                            A square tile holds a photograph, which has to fill
                            its frame - "contain" would letterbox it inside the
                            card. The standing cutouts still need "contain", or
                            the figure would be cropped by its own box.
                          */
                          resizeMode={square ? 'cover' : 'contain'}
                        />
                      ) : null}
                    </View>

                    <Text
                      variant="caption"
                      align="center"
                      numberOfLines={2}
                      color={active ? colors.primary : colors.inkBody}
                    >
                      {category.label}
                    </Text>
                  </Pressable>
                );
              })}

              {/*
                Empty cells to pad a short final row, so the one or two real
                tiles stay the width of every tile above them.
              */}
              {Array.from({ length: cols - row.length }, (_, i) => (
                <View key={`pad-${i}`} style={{ width: tile }} />
              ))}
            </View>
          ))
        : null}
    </View>
  );
}

const styles = StyleSheet.create({
  /* The whole tile, label included, so picture and name fade together. */
  dim: {
    opacity: 0.45,
  },
  grid: {
    gap: spacing.sm,
  },
  row: {
    flexDirection: 'row',
  },
  cell: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  /* Centred on the tile and lifted above it; the panel hides the rest. */
  art: {
    position: 'absolute',
  },
  /*
   * Fills the tile box and is painted after the artwork, which is what crops
   * the figure. Opaque on purpose - a translucent panel would show the legs
   * ghosting through it.
   */
  /* Sized and centred by the caller; only the bottom edge is fixed here, so
     the card and the artwork share a floor. */
  panel: {
    position: 'absolute',
    bottom: 0,
    borderRadius: radii.lg,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.surfaceAccent,
    ...hairline,
  },
  panelActive: {
    borderColor: colors.primary,
    borderWidth: 2,
  },
  /* Rounded to the same corner the card uses, since here the picture *is* the
     card - there is nothing behind it to round. */
  artSquare: {
    borderRadius: radii.md,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default React.memo(CategoryGrid);
